import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  formatBillingOfferAmount,
  formatBillingOfferPrice,
  getBillingOffer,
  getSubscriptionOffers,
} from "@/features/billing/billingOffers";
import type { BillingOffer, BillingOfferCode, BillingInterval } from "@/features/billing/billing.types";

type BillingPlanGridProps = {
  currentPlanOfferCode: BillingOfferCode | null;
  interval: BillingInterval;
  onCheckout: (offerCode: BillingOfferCode) => void;
  onIntervalChange: (interval: BillingInterval) => void;
  offers: BillingOffer[];
};

export function BillingPlanGrid({
  currentPlanOfferCode,
  interval,
  onCheckout,
  onIntervalChange,
  offers,
}: BillingPlanGridProps) {
  const { i18n, t } = useTranslation();
  const plans = getSubscriptionOffers(offers).filter((offer) => offer.billingInterval === interval);

  return (
    <div className="space-y-8">
      <div className="space-y-5">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink/78">
            {t("billing.plans.eyebrow")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <p className="text-lg text-ink/72">
            {t("billing.plans.billingLabel", { defaultValue: "Billing:" })}
          </p>
          <div className="inline-flex rounded-[1.15rem] bg-[#f3ede6] p-1">
            {(["monthly", "annual"] as const).map((value) => (
              <button
                key={value}
                className={`rounded-[0.95rem] px-6 py-3 text-base font-semibold transition ${
                  value === interval
                    ? "bg-white text-ink shadow-[0_8px_20px_-18px_rgba(17,13,10,0.7)]"
                    : "text-ink/55 hover:text-ink"
                }`}
                onClick={() => onIntervalChange(value)}
                type="button"
              >
                {t(`billing.interval.${value}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-10 xl:grid-cols-2">
        {plans.map((offer) => {
          const isCurrent = currentPlanOfferCode === offer.offerCode;
          const alternateOfferCode = offer.billingInterval === "monthly"
            ? `${offer.tier}_annual`
            : `${offer.tier}_monthly`;
          const alternateOffer = getBillingOffer(alternateOfferCode as BillingOfferCode, offers);
          const amountPart = formatBillingOfferAmount(
            offer,
            i18n.resolvedLanguage ?? i18n.language,
          );
          const cadencePart = t(`billing.interval.${offer.billingInterval}`);
          const actionLabel = currentPlanOfferCode
            ? t("billing.plans.change", { defaultValue: "Change plan" })
            : t("billing.plans.choose");

          return (
            <article
              key={offer.offerCode}
              className={[
                "flex h-full flex-col justify-between gap-8 border-b border-[#ece2d6] pb-6",
                isCurrent ? "border-l-[3px] border-l-[#2e2721] pl-6" : "pl-7",
              ].join(" ")}
            >
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="flex flex-wrap items-end gap-3">
                    <p className="font-display text-[clamp(3rem,6vw,4.4rem)] leading-none text-ink">
                      {amountPart.trim()}
                    </p>
                    {cadencePart ? (
                      <p className="pb-2 text-[1.85rem] font-medium text-ink/55">
                        / {cadencePart.trim()}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <h3 className="text-[2rem] font-semibold leading-tight text-ink">
                      {t(`common.tiers.${offer.tier}`)}
                    </h3>
                    <p className="mt-2 text-sm text-ink/58">
                      {alternateOffer
                        ? t("billing.plans.alternateHint", {
                            price: formatBillingOfferPrice(alternateOffer, {
                              intervalLabel:
                                alternateOffer.kind === "subscription"
                                  ? t(`billing.interval.${alternateOffer.billingInterval}`)
                                  : null,
                              locale: i18n.resolvedLanguage ?? i18n.language,
                            }),
                          })
                        : t("billing.plans.subtitle")}
                    </p>
                  </div>
                </div>

                <div className="space-y-5">
                  {offer.features.map((feature) => (
                    <div key={feature.labelKey} className="flex items-start gap-4 text-sm text-ink/74">
                      <Check className="mt-0.5 size-5 text-[#9a7b54]" />
                      <div>
                        <p className="text-[1.05rem] font-semibold text-ink">
                          {t(feature.labelKey, feature.labelValues)}
                        </p>
                        <p className="mt-1 leading-7 text-ink/62">
                          {t(feature.descriptionKey, feature.descriptionValues)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#ece2d6] pt-6">
                <div className="min-h-[1.5rem] text-sm font-semibold text-[#9a7b54]">
                  {isCurrent ? t("billing.currentPlan.action") : null}
                </div>
                <Button
                  className="min-w-[11.5rem] rounded-[1rem] bg-[#332c26] px-8 py-3.5 text-base text-white hover:bg-[#261f1a]"
                  disabled={isCurrent}
                  onClick={() => onCheckout(offer.offerCode)}
                  type="button"
                  variant={isCurrent ? "outline" : "default"}
                >
                  {isCurrent ? t("billing.currentPlan.action") : actionLabel}
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
